/**
 * Custom hook untuk mengambil data occupancy harian.
 *
 * API Endpoint: GET `/api/v4/occupancy-daily`
 * API Version: 4.0
 *
 * Response v4 (array) diadaptasi kembali ke bentuk lama v3 (object keyed by tanggal)
 * agar konsumen (mapOccCalendar, CalendarPage, RoomMaintenancePage) tidak perlu berubah.
 *
 * @param {string} gte - Tanggal awal (filter[start])
 * @param {string} lte - Tanggal akhir (filter[end])
 * @example
 * const { data, isLoading, error } = useOccupancy({ gte: '2022-01-01', lte: '2022-01-31' });
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { OccupancyResponse } from "../response/OccupancyResponse";
import { OccupancyDailyResponseSchema } from "../../occupancy/response/OccupancyDailyResponse";
import { useAuth } from "../../../auth/context/AuthProvider";

const API_VERSION = 'v4';

interface OccupancyProps {
    gte: string;
    lte: string;
}

const useOccupancy = ({ gte, lte }: OccupancyProps) => {
    const { user } = useAuth();
    // /v4/occupancy-daily mewajibkan filter[property_id] (v3 memakai active property di backend)
    const propertyId = user?.active_property_id;

    return useQuery({
        queryKey: ['bookings', 'monthly', 'calendar', 'occupancy', gte, lte, propertyId],
        queryFn: async (): Promise<OccupancyResponse> => {
            try {
                const response = await privateApi.get(`/${API_VERSION}/occupancy-daily`, {
                    params: {
                        filter: {
                            start: gte,
                            end: lte,
                            property_id: propertyId,
                        }
                    }
                });

                // Validasi response v4
                const validationResult = OccupancyDailyResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Occupancy data format");
                }

                const v4 = validationResult.data;
                // Adaptasi ke bentuk v3: object keyed by tanggal.
                // total_maintenance_rooms tidak tersedia di v4 (sudah dikurangkan ke total_rooms) — diisi 0.
                const data = Object.fromEntries(
                    v4.data.map((item) => [item.date, {
                        total_rooms: String(item.total_rooms),
                        total_booked: item.total_booked,
                        occupancy: item.occupancy,
                        total_maintenance_rooms: 0,
                    }])
                );

                return {
                    success: v4.success,
                    message: v4.message,
                    code: v4.code,
                    data,
                };
            } catch (error) {
                console.error(`Failed to fetch Occupancy`, error);
                throw error;
            }
        },
        enabled: !!gte && !!lte && !!propertyId,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false
    });
};

export default useOccupancy;
